/**
 * Extracts the metric value for the currently active sort field from a player
 *
 * Maps SortField enum values to the corresponding player bioData field
 * and formats the value as a rounded-sm integer string. Used by the shared
 * PlayerSearchResultRow to display the dynamic metric badge.
 * @module components/shared/search-modal/utils/getMetricValue
 */

import {
  type PlayerSearchPlayerFragment,
  SortField,
} from "@/generated/graphql";
import {
  extractCustomMetricId,
  isCustomMetricSort,
} from "@/utils/customMetricsSorting";
import {
  formatCurrency,
  getContractRemainingMonths,
} from "@/utils/playerFormatting";

/**
 * Extracts the metric value for the currently active sort field from a player
 * @param player - Player fragment from search results
 * @param sortField - Active sort field (SortField enum value or custom metric key)
 * @returns Formatted metric string or null
 */
const getMetricValue = (
  player: PlayerSearchPlayerFragment,
  sortField: string
): string | null => {
  if (isCustomMetricSort(sortField)) {
    const statName = extractCustomMetricId(sortField);
    const customStats = player.bioData?.customStats as Record<
      string,
      { value?: string }
    > | null;
    const val = customStats?.[statName]?.value;
    return val != null ? Number(val).toFixed(0) : null;
  }

  const valueByField: Record<string, string | null | undefined> = {
    [SortField.Gpr]: player.bioData?.gpr?.toFixed(0),
    [SortField.FitScore]:
      player.bioData?.fitScore != null
        ? Math.round(player.bioData.fitScore * 100).toFixed(0)
        : null,
    [SortField.TeamStyleFit]: player.bioData?.teamStyleFit?.toFixed(0),
    [SortField.Carrying]:
      player.bioData?.carryingTransGlobalCategoricalScore?.toFixed(0),
    [SortField.Dribbling]:
      player.bioData?.dribblingTransGlobalCategoricalScore?.toFixed(0),
    [SortField.Passing]:
      player.bioData?.passingTransGlobalCategoricalScore?.toFixed(0),
    [SortField.Shooting]:
      player.bioData?.shotLocationTransGlobalCategoricalScore?.toFixed(0),
    [SortField.GkPassing]:
      player.bioData?.gkPassingTransCategoricalScore?.toFixed(0),
    [SortField.GkPositioning]:
      player.bioData?.gkPositioningTransCategoricalScore?.toFixed(0),
    [SortField.GkShotStopping]:
      player.bioData?.gkShotStoppingTransCategoricalScore?.toFixed(0),
    [SortField.MonthsRemaining]:
      player.bioData?.contractExpires != null
        ? String(getContractRemainingMonths(player.bioData.contractExpires))
        : null,
    [SortField.PlayerValuation]:
      player.bioData?.playerValuation != null
        ? formatCurrency(player.bioData.playerValuation)
        : null,
  };
  return valueByField[sortField] ?? null;
};

export default getMetricValue;
